import { useMemo } from "react";

import {
  faArrowDownWideShort,
  faArrowUpWideShort,
} from "@fortawesome/pro-solid-svg-icons";
import { Column } from "@tanstack/react-table";
import { isString, uniq } from "lodash-es";

import { ColumnFilter } from "src/base-components/ColumnFilter";
import { Icon } from "src/base-components/Icon";
import {
  EntityMapping,
  FeatureQueryStatus,
} from "src/clients/features-control/api";
import { Feature, Query } from "src/featureCatalogue/utils";

type FilterableRow = Feature | Query;

type GenericColumnType<T> = Column<T, unknown>;

export const StatusFilter = <T extends FilterableRow>({
  column,
}: {
  column: GenericColumnType<T>;
}): React.ReactElement => {
  const filters = column.getFilterValue();

  return (
    <ColumnFilter
      description="Status filter:"
      elements={[
        {
          key: FeatureQueryStatus.ACTIVE,
          value: "active",
        },
        {
          key: FeatureQueryStatus.INACTIVE,
          value: "inactive",
        },
      ]}
      isFiltering={column.getIsFiltered()}
      selected={filters}
      onResetRequest={() => column.setFilterValue(undefined)}
      onSelect={column.setFilterValue}
    />
  );
};

export const EventTypeFilter = <T extends FilterableRow>({
  column,
}: {
  column: GenericColumnType<T>;
}): React.ReactElement => {
  const filters = column.getFilterValue();
  const facetedValues = column.getFacetedUniqueValues();

  const elements = useMemo(
    () =>
      Array.from(facetedValues.keys()).map((key) => ({
        key: key,
        value: key,
      })),
    [facetedValues],
  );

  return (
    <ColumnFilter
      description="Event type filter:"
      elements={elements}
      isFiltering={column.getIsFiltered()}
      selected={filters}
      onResetRequest={() => column.setFilterValue(undefined)}
      onSelect={column.setFilterValue}
    />
  );
};

export const EntityFilter = <T extends FilterableRow>({
  column,
  type,
}: {
  column: GenericColumnType<T>;
  type?: "feature" | "query";
}): React.ReactElement => {
  const filters = column.getFilterValue();
  const facetedRowModel = column.getFacetedRowModel();

  const elements = useMemo(() => {
    let entityNames: string[];
    if (type === "query") {
      // For queries, entities are in columns.entities structure
      entityNames = uniq(
        facetedRowModel.flatRows
          .flatMap((row) =>
            (row.original as Query)?.columns?.entities?.map(
              (entity: EntityMapping) => entity.entity_name,
            ),
          )
          .filter(isString),
      );
    } else {
      // For features, entities are in entity_types structure
      entityNames = uniq(
        facetedRowModel.flatRows
          .flatMap((row) => (row.original as Feature)?.entity_types)
          .filter(isString),
      );
    }

    return entityNames.map((entity) => ({
      key: entity,
      value: entity,
    }));
  }, [facetedRowModel, type]);

  return (
    <ColumnFilter
      description="Entity type filter:"
      elements={elements}
      isFiltering={column.getIsFiltered()}
      selected={filters}
      onResetRequest={() => column.setFilterValue(undefined)}
      onSelect={column.setFilterValue}
    />
  );
};

export const SortActionButton = <T,>({
  column,
}: {
  column: GenericColumnType<T>;
}) => {
  const isSorted = column.getIsSorted();

  return (
    <button
      className="flex items-center"
      type="button"
      onClick={() => column.toggleSorting(isSorted === "asc" ? true : false)}
    >
      <Icon
        color={
          isSorted ? "text-indigo-500" : "text-gray-500 hover:text-gray-900"
        }
        icon={isSorted === "desc" ? faArrowDownWideShort : faArrowUpWideShort}
        size="xs"
      />
    </button>
  );
};
